import { api } from '@/lib/api';
import { LevelResponse, CreateLevelRequest, UpdateLevelRequest, LevelQuery } from '../types';
import { ApiResponse, LevelListResponse } from '@troia-v3/types';

const BASE_URL = '/levels';

export const levelsService = {
  /**
   * Get all levels with pagination and filters
   */
  async getAll(params?: LevelQuery): Promise<LevelListResponse> {
    const response = await api.get<ApiResponse<LevelListResponse>>(BASE_URL, { params });
    return response.data.data!;
  },

  /**
   * Get level by ID
   */
  async getById(id: string): Promise<LevelResponse> {
    const response = await api.get<ApiResponse<LevelResponse>>(`${BASE_URL}/${id}`);
    return response.data.data!;
  },

  /**
   * Create new level
   */
  async create(data: CreateLevelRequest): Promise<LevelResponse> {
    const response = await api.post<ApiResponse<LevelResponse>>(BASE_URL, data);
    return response.data.data!;
  },

  /**
   * Update level
   */
  async update(id: string, data: UpdateLevelRequest): Promise<LevelResponse> {
    const response = await api.put<ApiResponse<LevelResponse>>(`${BASE_URL}/${id}`, data);
    return response.data.data!;
  },

  /**
   * Delete level
   */
  async delete(id: string): Promise<void> {
    await api.delete(`${BASE_URL}/${id}`);
  },

  /**
   * Get active levels only
   */
  async getActive(): Promise<LevelResponse[]> {
    const response = await api.get<ApiResponse<LevelListResponse>>(`${BASE_URL}/active`);
    // Backend returns ListResponse with items array
    return response.data.data?.items || [];
  },

  /**
   * Get levels for dropdown (public - no permissions required)
   * Returns only id and name for use in select/dropdown components
   */
  async getDropdown(): Promise<LevelResponse[]> {
    const response = await api.get<ApiResponse<LevelResponse[]>>(`${BASE_URL}/dropdown`);
    return response.data.data || [];
  },

  /**
   * Activate level
   */
  async activate(id: string): Promise<LevelResponse> {
    const response = await api.post<ApiResponse<LevelResponse>>(`${BASE_URL}/${id}/activate`);
    return response.data.data!;
  },

  /**
   * Deactivate level
   */
  async deactivate(id: string): Promise<LevelResponse> {
    const response = await api.post<ApiResponse<LevelResponse>>(`${BASE_URL}/${id}/deactivate`);
    return response.data.data!;
  },

  /**
   * Seed default levels (for setup)
   */
  async seedDefaults(): Promise<void> {
    await api.post(`${BASE_URL}/seed`);
  }
};
